import { AmountValueLocalized, Translate } from '@components'

import { BadgeWithLabel, Button, Color, SkeletonBox, SkeletonText } from '@billy/design-system'
import map from 'lodash/map'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EmberRoute } from '../../../../enums/emberRoute'
import { useEmberRouteUrl } from '../../../../hooks/routing/useEmberRouteUrl'
import { getQueryParamsString } from '../../../../utils/getQueryParamsString'
import { widgetId } from '../../constants/widgetId'
import { Widget } from '../../elements/Widget'
import { useWidgetsActions } from '../../hooks/useWidgetsActions'
import { invoiceStateToTranslationKey } from './constants/invoiceStateToTranslationKey'
import { OnboardingEmptyState } from './elements/OnboardingEmptyState'
import { InvoicesActionPoint } from './enums/invoicesActionPoint'
import { useInvoiceStats } from './hooks/useInvoiceStats'
import { useInvoiceWidgetData } from './hooks/useInvoiceWidgetData'
import { InvoiceItem } from './InvoiceItem'
import * as Styled from './styles'
import { InvoiceState } from './types/invoiceState'
import { StatsProps } from './types/statsProps'

export const Invoices = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { url: invoicesUrl } = useEmberRouteUrl(EmberRoute.Invoices)
  const { url: newInvoiceUrl } = useEmberRouteUrl(EmberRoute.InvoicesNew)
  const { trackDashboardActionPerformed } = useWidgetsActions()
  const isExternalInvoicesSource = organization?.source !== 'billy'

  const { summary, externalInvoicesSummary, isLoading, shouldShowEmptyState, doInvoicesExist } = useInvoiceWidgetData()

  const stats = useInvoiceStats({
    summary,
    externalInvoicesSummary,
    isExternalInvoicesSource,
  })

  const getCurrentAction = (invoiceState: InvoiceState) => {
    switch (invoiceState) {
      case 'draft':
        return InvoicesActionPoint.Draft
      case 'overdue':
        return InvoicesActionPoint.Overdue
      case 'paid':
        return InvoicesActionPoint.Paid
      case 'unpaid':
        return InvoicesActionPoint.Unpaid
    }
  }

  const handleValueClick = (invoiceState: InvoiceState) => {
    const queryParams = { status: invoiceState }
    const queryParamsString = getQueryParamsString(queryParams)
    navigate(`${invoicesUrl}?${queryParamsString}`)
    trackDashboardActionPerformed({ action: getCurrentAction(invoiceState), widgetId: widgetId.Invoices })
  }

  const handleCreateInvoiceClick = () => {
    trackDashboardActionPerformed({ action: InvoicesActionPoint.CreateInvoice, widgetId: widgetId.Invoices })

    if (newInvoiceUrl) {
      navigate(newInvoiceUrl)
    }
  }

  return (
    <Widget
      title={!shouldShowEmptyState ? t('dashboard.widget.invoices.title') : undefined}
      sideActions={
        doInvoicesExist && (
          <Button variant="outlined" onClick={handleCreateInvoiceClick}>
            <Translate value="dashboard.widget.invoices.create" />
          </Button>
        )
      }
      backgroundColor={shouldShowEmptyState ? Color.Orange50 : Color.White}
    >
      {isLoading ? (
        <SkeletonBox fullWidth height={150} />
      ) : (
        <>
          {shouldShowEmptyState && <OnboardingEmptyState />}
          {doInvoicesExist && (
            <>
              <Styled.InvoicesList>
                {map(stats, (stat: StatsProps, key: InvoiceState) => {
                  const { badgeColor, count, amount } = stat
                  const invoiceStateTranslationKey = invoiceStateToTranslationKey[key]

                  return (
                    <InvoiceItem invoiceState={key} key={key} onClick={handleValueClick}>
                      <Styled.BadgeWrapper>
                        <Styled.BadgeWrapper>
                          <BadgeWithLabel color={badgeColor} badge={count}>
                            {t(invoiceStateTranslationKey)}
                          </BadgeWithLabel>
                        </Styled.BadgeWrapper>
                      </Styled.BadgeWrapper>
                      {isLoading ? (
                        <SkeletonText variant="h1" width={110} />
                      ) : (
                        <AmountValueLocalized
                          amount={amount}
                          currencyId={organization?.baseCurrencyId}
                          textVariant="h1"
                          shortFormat
                        />
                      )}
                    </InvoiceItem>
                  )
                })}
              </Styled.InvoicesList>
            </>
          )}
        </>
      )}
    </Widget>
  )
}
